import React, { useEffect, useState } from "react";
import { Modal, Button, ListGroup } from "react-bootstrap";
import "animate.css";

import { putProducto } from "../helpers/rutaProductos";
import { puedeVerCostosFront } from "../helpers/permisosCostos";
import { getCategoriasProductos } from "../helpers/rutaCategoriasProductos";
import { getMarcas } from "../helpers/rutaMarcas";
import { getColores } from "../helpers/rutaColores";

/* ---------------- utils ---------------- */
const clean = (v) => String(v ?? "").trim();

const upper = (v) => clean(v).toUpperCase();

const toNumberMinZero = (v) => {
  const n = Number(v);
  if (!Number.isFinite(n)) return 0;
  return n < 0 ? 0 : n;
};

const normalizeTipoMotor = (v) => {
  const t = upper(v)
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");

    if (t === "HIBRIDO" || t === "HIBRIDA") {
      return "HIBRIDA";
    }

    if (
      ["COMBUSTION", "ELECTRICA", "NO_APLICA"].includes(t)
    ) {
      return t;
    }

  return "NO_APLICA";
};

const getId = (v) => {
  if (!v) return "";
  if (typeof v === "object") return clean(v._id ?? v.id ?? v.value ?? "");
  return clean(v);
};

const toArrayIds = (arr) => {
  if (!Array.isArray(arr)) return [];
  return arr.map((x) => getId(x)).filter(Boolean);
};

/* ---------------- categorias ---------------- */
const unwrapCategorias = (r) => {
  if (Array.isArray(r?.categorias)) return r.categorias;
  if (Array.isArray(r?.categoriasProductos)) return r.categoriasProductos;
  if (Array.isArray(r?.data?.categorias)) return r.data.categorias;
  if (Array.isArray(r?.data?.categoriasProductos)) return r.data.categoriasProductos;
  if (Array.isArray(r?.data)) return r.data;
  if (Array.isArray(r)) return r;
  return [];
};

const getCategoriaNombre = (c) =>
  clean(c?.nombre) ||
  clean(c?.categoria) ||
  clean(c?.descripcion) ||
  clean(c?.label) ||
  "-";

const getCategoriaCodigo = (c) =>
  clean(c?.codigo) ||
  clean(c?.codcategoria) ||
  clean(c?.codcat) ||
  clean(c?.cod) ||
  "";

/* ---------------- marcas ---------------- */
const unwrapMarcas = (r) => {
  if (Array.isArray(r?.marcas)) return r.marcas;
  if (Array.isArray(r?.data?.marcas)) return r.data.marcas;
  if (Array.isArray(r?.data)) return r.data;
  if (Array.isArray(r)) return r;
  return [];
};

const getMarcaNombre = (m) =>
  clean(m?.marca) ||
  clean(m?.nombre) ||
  clean(m?.descripcion) ||
  clean(m?.label) ||
  "-";

const getMarcaCodigo = (m) =>
  clean(m?.codmarca) ||
  clean(m?.codigo) ||
  clean(m?.cod) ||
  "";

/* ---------------- colores ---------------- */
const unwrapColores = (r) => {
  if (Array.isArray(r?.colores)) return r.colores;
  if (Array.isArray(r?.data?.colores)) return r.data.colores;
  if (Array.isArray(r?.data)) return r.data;
  if (Array.isArray(r)) return r;
  return [];
};

const getColorNombre = (c) =>
  clean(c?.color) ||
  clean(c?.nombre) ||
  clean(c?.descripcion) ||
  clean(c?.label) ||
  "-";

const getColorCodigo = (c) =>
  clean(c?.codcolor) ||
  clean(c?.codigo) ||
  clean(c?.cod) ||
  "";

const initial = {
  codprod: "",

  categoria: "",
  marca: "",

  modelo: "",
  nombreComercial: "",
  descripcion: "",

  tipoMotor: "NO_APLICA",

  cilindradaCC: "",
  potenciaWatts: "",
  potenciaKw: "",
  tensionVolt: "",
  capacidadAh: "",
  capacidadKwh: "",
  cantidadPaneles: "",
  potenciaPanelWatts: "",

  usaColor: false,
  coloresDisponibles: [],

  precioVentaUSD: "",
  precioMinimoUSD: "",
  costoProductoUSD: "",
  comisionBaseUSD: "",
  costoEnvioUSD: "",

  politicaEnvio: "NO_APLICA",
  permiteEnvio: true,
  demoraEntrega: "",
  aclaracion: "",

  especificaciones: [],

  observaciones: "",
  activo: true,
};

const ModalFormProducto = ({ producto, handleClose }) => {
  const puedeVerCostos = puedeVerCostosFront();
  const [showSuccess, setShowSuccess] = useState(false);

  const data = producto?.producto || producto?.productos || producto || {};

  const [formValues, setFormValues] = useState(initial);

  const [categorias, setCategorias] = useState({
    data: [],
    loading: true,
  });

  const [marcas, setMarcas] = useState({
    data: [],
    loading: true,
  });

  const [colores, setColores] = useState({
    data: [],
    loading: true,
  });

  const [specNombre, setSpecNombre] = useState("");
  const [specValor, setSpecValor] = useState("");

  useEffect(() => {
    const categoriaId = getId(data?.categoria);
    const marcaId = getId(data?.marca);

    setFormValues({
      codprod: data?.codprod ?? "",

      categoria: categoriaId,
      marca: marcaId,

      modelo: data?.modelo || "",
      nombreComercial: data?.nombreComercial || "",
      descripcion: data?.descripcion || "",

      tipoMotor: data?.tipoMotor || "NO_APLICA",

      cilindradaCC: data?.cilindradaCC ?? "",
      potenciaWatts: data?.potenciaWatts ?? "",
      potenciaKw: data?.potenciaKw ?? "",
      tensionVolt: data?.tensionVolt ?? "",
      capacidadAh: data?.capacidadAh ?? "",
      capacidadKwh: data?.capacidadKwh ?? "",
      cantidadPaneles: data?.cantidadPaneles ?? "",
      potenciaPanelWatts: data?.potenciaPanelWatts ?? "",

      usaColor: data?.usaColor ?? false,
      coloresDisponibles: toArrayIds(data?.coloresDisponibles),

      precioVentaUSD: data?.precioVentaUSD ?? "",
      precioMinimoUSD: data?.precioMinimoUSD ?? "",
      costoProductoUSD: data?.costoProductoUSD ?? "",
      comisionBaseUSD: data?.comisionBaseUSD ?? "",
      costoEnvioUSD: data?.costoEnvioUSD ?? "",

      politicaEnvio: data?.politicaEnvio || "NO_APLICA",
      permiteEnvio: data?.permiteEnvio ?? true,
      demoraEntrega: data?.demoraEntrega || "",
      aclaracion: data?.aclaracion || "",

      especificaciones: Array.isArray(data?.especificaciones)
        ? data.especificaciones
        : [],

      observaciones: data?.observaciones || "",
      activo: data?.activo ?? true,
    });
  }, [producto]);

  useEffect(() => {
    getCategoriasProductos({ limit: 10000 })
      .then((resp) => {
        setCategorias({
          data: unwrapCategorias(resp),
          loading: false,
        });
      })
      .catch((error) => {
        console.error("❌ Error al cargar categorías:", error);
        setCategorias({
          data: [],
          loading: false,
        });
      });

    getMarcas({ limit: 10000 })
      .then((resp) => {
        setMarcas({
          data: unwrapMarcas(resp),
          loading: false,
        });
      })
      .catch((error) => {
        console.error("❌ Error al cargar marcas:", error);
        setMarcas({
          data: [],
          loading: false,
        });
      });

    getColores({ limit: 10000 })
      .then((resp) => {
        setColores({
          data: unwrapColores(resp),
          loading: false,
        });
      })
      .catch((error) => {
        console.error("❌ Error al cargar colores:", error);
        setColores({
          data: [],
          loading: false,
        });
      });
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormValues((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleCheckChange = (e) => {
    const { name, checked } = e.target;

    setFormValues((prev) => ({
      ...prev,
      [name]: checked,
      ...(name === "usaColor" && !checked ? { coloresDisponibles: [] } : {}),
    }));
  };

  const toggleColor = (colorId) => {
    if (!colorId) return;

    setFormValues((prev) => {
      const actual = Array.isArray(prev.coloresDisponibles)
        ? prev.coloresDisponibles
        : [];

      const existe = actual.includes(colorId);

      return {
        ...prev,
        coloresDisponibles: existe
          ? actual.filter((x) => x !== colorId)
          : [...actual, colorId],
      };
    });
  };

  const addEspecificacion = () => {
    const nombre = upper(specNombre);
    const valor = upper(specValor);

    if (!nombre || !valor) {
      alert("Completá nombre y valor de la especificación.");
      return;
    }

    setFormValues((prev) => ({
      ...prev,
      especificaciones: [
        ...(Array.isArray(prev.especificaciones) ? prev.especificaciones : []),
        {
          nombre,
          valor,
        },
      ],
    }));

    setSpecNombre("");
    setSpecValor("");
  };

  const removeEspecificacion = (idx) => {
    setFormValues((prev) => ({
      ...prev,
      especificaciones: prev.especificaciones.filter((_, i) => i !== idx),
    }));
  };

  const validate = () => {
    if (!clean(formValues.categoria)) {
      return "Seleccioná una categoría.";
    }

    if (!clean(formValues.marca)) {
      return "Seleccioná una marca.";
    }

    if (!clean(formValues.modelo)) {
      return "Ingresá el modelo.";
    }

    if (!clean(formValues.nombreComercial)) {
      return "Ingresá el nombre comercial.";
    }

    if (!clean(formValues.descripcion)) {
      return "Ingresá la descripción.";
    }

    if (formValues.usaColor && !formValues.coloresDisponibles.length) {
      return "Marcaste que usa color, pero no seleccionaste colores.";
    }

    return "";
  };

const buildPayload = () => {
  const payload = {
      // codprod: Number(formValues.codprod || 0),

      categoria: clean(formValues.categoria),
      marca: clean(formValues.marca),

      modelo: upper(formValues.modelo),
      nombreComercial: upper(formValues.nombreComercial),
      descripcion: upper(formValues.descripcion),

      tipoMotor: normalizeTipoMotor(formValues.tipoMotor || "NO_APLICA"),

      cilindradaCC: toNumberMinZero(formValues.cilindradaCC),
      potenciaWatts: toNumberMinZero(formValues.potenciaWatts),
      potenciaKw: toNumberMinZero(formValues.potenciaKw),
      tensionVolt: toNumberMinZero(formValues.tensionVolt),
      capacidadAh: toNumberMinZero(formValues.capacidadAh),
      capacidadKwh: toNumberMinZero(formValues.capacidadKwh),
      cantidadPaneles: toNumberMinZero(formValues.cantidadPaneles),
      potenciaPanelWatts: toNumberMinZero(formValues.potenciaPanelWatts),

      usaColor: !!formValues.usaColor,
      coloresDisponibles: formValues.usaColor
        ? formValues.coloresDisponibles
        : [],

      precioVentaUSD: toNumberMinZero(formValues.precioVentaUSD),
      precioMinimoUSD: toNumberMinZero(formValues.precioMinimoUSD),
      comisionBaseUSD: toNumberMinZero(formValues.comisionBaseUSD),
      costoEnvioUSD: toNumberMinZero(formValues.costoEnvioUSD),

      politicaEnvio: upper(formValues.politicaEnvio || "NO_APLICA"),
      permiteEnvio: !!formValues.permiteEnvio,
      demoraEntrega: upper(formValues.demoraEntrega),
      aclaracion: upper(formValues.aclaracion),

      especificaciones: Array.isArray(formValues.especificaciones)
        ? formValues.especificaciones.map((e) => ({
            nombre: upper(e.nombre),
            valor: upper(e.valor),
          }))
        : [],

      observaciones: upper(formValues.observaciones),
      activo: !!formValues.activo,
    };
    if (puedeVerCostos) {
      payload.costoProductoUSD =
        toNumberMinZero(
          formValues.costoProductoUSD
        );
    }

    return payload;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const id = data?._id || producto?._id;

    if (!id) {
      alert("No se encontró el id del producto.");
      return;
    }

    const msg = validate();
    if (msg) {
      alert(msg);
      return;
    }

    const payload = buildPayload();

    try {
      const resp = await putProducto(id, payload);

      console.log("✅ Producto actualizado:", resp);

      if (resp?.ok === false) {
        alert(
          resp?.error?.message ||
            resp?.error ||
            "No se pudo actualizar el producto"
        );
        return;
      }

      setShowSuccess(true);

      setTimeout(() => {
        setShowSuccess(false);
        handleClose();
      }, 900);
    } catch (error) {
      const dataError = error?.response?.data;

      console.error("❌ Error al guardar producto:", dataError || error);

      const mensaje =
        dataError?.message ||
        dataError?.mensaje ||
        dataError?.error?.message ||
        dataError?.error ||
        "Error al guardar producto. Revisá consola / Network.";

      alert(mensaje);
    }
  };

  if (categorias.loading || marcas.loading || colores.loading) {
    return <div className="p-3">Cargando...</div>;
  }

  return (
    <>
      <form onSubmit={handleSubmit} noValidate>
        <Modal.Body>
          <div className="form-row">
            <div className="form-group mt-3 col-sm-2">
              <label>Código</label>
              <input
                type="number"
                className="form-control"
                name="codprod"
                value={formValues.codprod}
                readOnly
              />
            </div>

            <div className="form-group mt-3 col-sm-5">
              <label>Modelo</label>
              <input
                type="text"
                className="form-control"
                name="modelo"
                maxLength="80"
                required
                value={formValues.modelo}
                onChange={handleChange}
              />
            </div>

            <div className="form-group mt-3 col-sm-5">
              <label>Nombre Comercial</label>
              <input
                type="text"
                className="form-control"
                name="nombreComercial"
                maxLength="120"
                required
                value={formValues.nombreComercial}
                onChange={handleChange}
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group mt-3 col-sm-6">
              <label>Categoría</label>
              <select
                className="form-control"
                name="categoria"
                value={formValues.categoria}
                onChange={handleChange}
                required
              >
                <option value="">Elija la opción adecuada</option>

                {categorias.data
                  .filter((c) => c?.activo ?? true)
                  .sort((a, b) =>
                    getCategoriaNombre(a).localeCompare(
                      getCategoriaNombre(b),
                      "es",
                      { sensitivity: "base" }
                    )
                  )
                  .map((categoria) => {
                    const id = getId(categoria);
                    const codigo = getCategoriaCodigo(categoria);
                    const nombre = getCategoriaNombre(categoria);

                    return (
                      <option key={id} value={id}>
                        {codigo ? `${codigo} - ${nombre}` : nombre}
                      </option>
                    );
                  })}
              </select>
            </div>

            <div className="form-group mt-3 col-sm-6">
              <label>Marca</label>
              <select
                className="form-control"
                name="marca"
                value={formValues.marca}
                onChange={handleChange}
                required
              >
                <option value="">Elija la opción adecuada</option>

                {marcas.data
                  .filter((m) => m?.activo ?? true)
                  .sort((a, b) =>
                    getMarcaNombre(a).localeCompare(getMarcaNombre(b), "es", {
                      sensitivity: "base",
                    })
                  )
                  .map((marca) => {
                    const id = getId(marca);
                    const codigo = getMarcaCodigo(marca);
                    const nombre = getMarcaNombre(marca);

                    return (
                      <option key={id} value={id}>
                        {codigo ? `${codigo} - ${nombre}` : nombre}
                      </option>
                    );
                  })}
              </select>
            </div>
          </div>

          <div className="form-group mt-3">
            <label>Descripción</label>
            <textarea
              className="form-control"
              name="descripcion"
              rows="2"
              maxLength="300"
              required
              value={formValues.descripcion}
              onChange={handleChange}
            />
          </div>

          <div className="form-row">
            <div className="form-group mt-3 col-sm-3">
              <label>Tipo Motor</label>
              <select
                className="form-control"
                name="tipoMotor"
                value={formValues.tipoMotor}
                onChange={handleChange}
              >
                <option value="NO_APLICA">NO APLICA</option>
                <option value="COMBUSTION">COMBUSTIÓN</option>
                <option value="ELECTRICA">ELECTRICA</option>
                <option value="HIBRIDA">HÍBRIDO</option>
              </select>
            </div>

            <div className="form-group mt-3 col-sm-3">
              <label>Cilindrada CC</label>
              <input
                type="number"
                className="form-control"
                name="cilindradaCC"
                value={formValues.cilindradaCC}
                onChange={handleChange}
              />
            </div>

            <div className="form-group mt-3 col-sm-3">
              <label>Potencia Watts</label>
              <input
                type="number"
                className="form-control"
                name="potenciaWatts"
                value={formValues.potenciaWatts}
                onChange={handleChange}
              />
            </div>

            <div className="form-group mt-3 col-sm-3">
              <label>Potencia Kw</label>
              <input
                type="number"
                className="form-control"
                name="potenciaKw"
                value={formValues.potenciaKw}
                onChange={handleChange}
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group mt-3 col-sm-3">
              <label>Tensión Volt</label>
              <input
                type="number"
                className="form-control"
                name="tensionVolt"
                value={formValues.tensionVolt}
                onChange={handleChange}
              />
            </div>

            <div className="form-group mt-3 col-sm-3">
              <label>Capacidad Ah</label>
              <input
                type="number"
                className="form-control"
                name="capacidadAh"
                value={formValues.capacidadAh}
                onChange={handleChange}
              />
            </div>

            <div className="form-group mt-3 col-sm-3">
              <label>Capacidad Kwh</label>
              <input
                type="number"
                className="form-control"
                name="capacidadKwh"
                value={formValues.capacidadKwh}
                onChange={handleChange}
              />
            </div>

            <div className="form-group mt-3 col-sm-3">
              <label>Cant. Paneles</label>
              <input
                type="number"
                className="form-control"
                name="cantidadPaneles"
                value={formValues.cantidadPaneles}
                onChange={handleChange}
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group mt-3 col-sm-3">
              <label>Potencia Panel Watts</label>
              <input
                type="number"
                className="form-control"
                name="potenciaPanelWatts"
                value={formValues.potenciaPanelWatts}
                onChange={handleChange}
              />
            </div>

            <div className="form-group mt-3 col-sm-3">
              <label>Precio Venta USD</label>
              <input
                type="number"
                className="form-control"
                name="precioVentaUSD"
                value={formValues.precioVentaUSD}
                onChange={handleChange}
              />
            </div>

            <div className="form-group mt-3 col-sm-3">
              <label>Precio Mínimo USD</label>
              <input
                type="number"
                className="form-control"
                name="precioMinimoUSD"
                value={formValues.precioMinimoUSD}
                onChange={handleChange}
              />
            </div>

            <div className="form-group mt-3 col-sm-3">
              <label>Comisión Base USD</label>
              <input
                type="number"
                className="form-control"
                name="comisionBaseUSD"
                value={formValues.comisionBaseUSD}
                onChange={handleChange}
              />
            </div>
          </div>

          <div className="form-row">
            {puedeVerCostos && (
              <div className="form-group mt-3 col-sm-3">
                <label>Costo Producto USD</label>

                <input
                  type="number"
                  className="form-control"
                  name="costoProductoUSD"
                  value={formValues.costoProductoUSD}
                  onChange={handleChange}
                  min="0"
                  step="0.01"
                  placeholder="0.00"
                />
              </div>
            )}

            <div className="form-group mt-3 col-sm-3">
              <label>Costo Envío USD</label>

              <input
                type="number"
                className="form-control"
                name="costoEnvioUSD"
                value={formValues.costoEnvioUSD}
                onChange={handleChange}
              />
            </div>

            <div className="form-group mt-3 col-sm-3">
              <label>Política Envío</label>

              <select
                className="form-control"
                name="politicaEnvio"
                value={formValues.politicaEnvio}
                onChange={handleChange}
              >
                <option value="NO_APLICA">
                  NO APLICA
                </option>

                <option value="INCLUIDO">
                  INCLUIDO
                </option>

                <option value="A_CARGO_CLIENTE">
                  A CARGO CLIENTE
                </option>

                <option value="A_COORDINAR">
                  A COORDINAR
                </option>
              </select>
            </div>

            <div className="form-group mt-3 col-sm-3">
              <label>Permite Envío</label>

              <div className="form-control d-flex align-items-center">
                <input
                  type="checkbox"
                  name="permiteEnvio"
                  checked={formValues.permiteEnvio}
                  onChange={handleCheckChange}
                  style={{ marginRight: 8 }}
                />
                Sí
              </div>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group mt-3 col-sm-3">
              <label>Activo</label>

              <div className="form-control d-flex align-items-center">
                <input
                  type="checkbox"
                  name="activo"
                  checked={formValues.activo}
                  onChange={handleCheckChange}
                  style={{ marginRight: 8 }}
                />
                Sí
              </div>
            </div>

            <div className="form-group mt-3 col-sm-9">
              <label>Demora Entrega</label>

              <input
                type="text"
                className="form-control"
                name="demoraEntrega"
                value={formValues.demoraEntrega}
                onChange={handleChange}
                placeholder="Ej: 15 DÍAS"
              />
            </div>
</div>

          <div className="form-row">
            <div className="form-group mt-3 col-sm-3">
              <label>Usa Color</label>
              <div className="form-control d-flex align-items-center">
                <input
                  type="checkbox"
                  name="usaColor"
                  checked={formValues.usaColor}
                  onChange={handleCheckChange}
                  style={{ marginRight: 8 }}
                />
                Sí
              </div>
            </div>

            <div className="form-group mt-3 col-sm-9">
              <label>Colores Disponibles</label>

              {!formValues.usaColor ? (
                <div className="form-control text-muted">No aplica</div>
              ) : (
                <div
                  className="border rounded p-2"
                  style={{ maxHeight: 160, overflowY: "auto" }}
                >
                  {colores.data.length === 0 ? (
                    <div className="text-muted">No hay colores cargados.</div>
                  ) : (
                    colores.data
                      .filter((c) => c?.activo ?? true)
                      .sort((a, b) =>
                        getColorNombre(a).localeCompare(
                          getColorNombre(b),
                          "es",
                          { sensitivity: "base" }
                        )
                      )
                      .map((color) => {
                        const id = getId(color);
                        const codigo = getColorCodigo(color);
                        const nombre = getColorNombre(color);
                        const checked =
                          formValues.coloresDisponibles.includes(id);

                        return (
                          <div
                            key={id}
                            className="d-flex align-items-center mb-1"
                          >
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={() => toggleColor(id)}
                              style={{ marginRight: 8 }}
                            />

                            <span>
                              {codigo ? `${codigo} - ${nombre}` : nombre}
                            </span>
                          </div>
                        );
                      })
                  )}
                </div>
              )}
            </div>
          </div>

          <hr />

          <div className="form-row">
            <div className="form-group mt-3 col-sm-4">
              <label>Especificación</label>
              <input
                type="text"
                className="form-control"
                value={specNombre}
                onChange={(e) => setSpecNombre(e.target.value)}
                placeholder="Ej: BATERÍA"
              />
            </div>

            <div className="form-group mt-3 col-sm-4">
              <label>Valor</label>
              <input
                type="text"
                className="form-control"
                value={specValor}
                onChange={(e) => setSpecValor(e.target.value)}
                placeholder="Ej: 72V"
              />
            </div>

            <div className="form-group mt-3 col-sm-4 d-flex align-items-end">
              <button
                type="button"
                className="btn btn-outline-dark"
                onClick={addEspecificacion}
              >
                Agregar Especificación
              </button>
            </div>
          </div>

          {!!formValues.especificaciones.length && (
            <div className="mt-3">
              <ListGroup>
                {formValues.especificaciones.map((e, idx) => (
                  <ListGroup.Item
                    key={`${e.nombre}-${idx}`}
                    className="d-flex justify-content-between align-items-center"
                  >
                    <span>
                      <b>{e.nombre}</b>: {e.valor}
                    </span>

                    <button
                      type="button"
                      className="btn btn-sm btn-outline-danger"
                      onClick={() => removeEspecificacion(idx)}
                    >
                      Quitar
                    </button>
                  </ListGroup.Item>
                ))}
              </ListGroup>
            </div>
          )}

          <div className="form-row">
            <div className="form-group mt-3 col-sm-6">
              <label>Aclaración</label>
              <textarea
                className="form-control"
                name="aclaracion"
                rows="2"
                value={formValues.aclaracion}
                onChange={handleChange}
              />
            </div>

            <div className="form-group mt-3 col-sm-6">
              <label>Observaciones</label>
              <textarea
                className="form-control"
                name="observaciones"
                rows="2"
                value={formValues.observaciones}
                onChange={handleChange}
              />
            </div>
          </div>
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={handleClose}>
            Cancelar
          </Button>

          <Button type="submit" variant="dark">
            Guardar
          </Button>
        </Modal.Footer>
      </form>

      <Modal
        show={showSuccess}
        centered
        backdrop="static"
        keyboard={false}
        dialogClassName="animate__animated animate__fadeInDown"
      >
        <Modal.Body className="text-center">
          <div style={{ fontSize: "3rem" }}>🖐</div>
          <h5 className="mt-2">¡Cambios guardados correctamente!</h5>
        </Modal.Body>
      </Modal>
    </>
  );
};

export default ModalFormProducto;